import React, { useState } from "react";
import { Checkbox } from "@opendate-app/setlist";
import "../../TeamManager/styles/teamManagerStyles.css";
import SettingsPageWrapper from "../../../shared/SettingsPageWrapper";
import { updateTeamSetting } from "../../../shared/api/team";

const HOLD_OPTIONS = Array.from({ length: 20 }, (_, i) => i + 1);

const PROMOTION_OPTIONS = [
  {
    value: 'HOLD_PROMOTE_AUTO_PROMOTE',
    label: 'Auto Promote',
    description: 'When clearing a hold on your calendar, all other lower holds on that date will move up in position.',
  },
  {
    value: 'HOLD_PROMOTE_ALWAYS_SEQUENTIAL',
    label: 'Auto Promote: Always Sequential',
    description: 'When a hold is deleted all remaining holds will be promoted and reordered to always be sequential.',
  },
  {
    value: 'HOLD_PROMOTE_NONE',
    label: "Don't Promote",
    description: 'When clearing a hold on your calendar, leave all other holds on that date at their original position.',
  },
];

const GROUPING_OPTIONS = [
  {
    value: 'by_venue',
    label: 'By Venue',
    description: 'Group all holds for a venue together (Venue A H1, Venue A H2, Venue B H1, Venue B H2).',
  },
  {
    value: 'by_position',
    label: 'By Position',
    description: 'Interleave holds across venues by position (Venue A H1, Venue B H1, Venue A H2, Venue B H2).',
  },
];

const SettingsHoldsDetails = ({ team, settingsPath, lastEditedAt, changelogVersions }) => {
  const [defaultTopHold, setDefaultTopHold] = useState(team.default_top_hold);
  const [holdPromotionLevel, setHoldPromotionLevel] = useState(team.hold_promotion_level);
  const [calendarHoldGrouping, setCalendarHoldGrouping] = useState(team.calendar_hold_grouping);
  const [appendAvailabilityDisclaimer, setAppendAvailabilityDisclaimer] = useState(
    team.append_availability_disclaimer_to_hold_dates ?? false
  );
  const [requireHoldRemovalReason, setRequireHoldRemovalReason] = useState(
    team.require_hold_removal_reason ?? true
  );

  const handleTopHoldChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setDefaultTopHold(value);
    updateTeamSetting(team.id, { default_top_hold: value });
  };

  const handlePromotionChange = (value) => {
    setHoldPromotionLevel(value);
    updateTeamSetting(team.id, { hold_promotion_level: value });
  };

  const handleGroupingChange = (value) => {
    setCalendarHoldGrouping(value);
    updateTeamSetting(team.id, { calendar_hold_grouping: value });
  };

  const handleAvailabilityDisclaimerChange = (e) => {
    const checked = e.target.checked;
    setAppendAvailabilityDisclaimer(checked);
    updateTeamSetting(team.id, { append_availability_disclaimer_to_hold_dates: checked });
  };

  const handleRequireHoldRemovalReasonChange = (e) => {
    const checked = e.target.checked;
    setRequireHoldRemovalReason(checked);
    updateTeamSetting(team.id, { require_hold_removal_reason: checked });
  };

  return (
    <SettingsPageWrapper settingsPath={settingsPath} title="Holds" lastEditedAt={lastEditedAt} changelogVersions={changelogVersions}>
      <div className="team-member-card">
        <div className="team-member-card-header">
          <div className="team-member-card-header-left">
            <div className="team-member-card-title-row">
              <h2 className="team-member-card-title">Holds</h2>
            </div>
            <p className="team-member-card-subtitle">
              Manage holds settings.
            </p>
          </div>
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />

        <div style={{ padding: '32px 24px' }}>
          <div>
            <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
              <strong>Default Top Hold</strong>
            </p>
            <p className="m-0 small text-muted">
              Select the default hold level when creating a new hold.
            </p>
          </div>
          <div className="mt-3">
            <select
              className="team-form-select"
              style={{ width: 80 }}
              value={defaultTopHold}
              onChange={handleTopHoldChange}
            >
              {HOLD_OPTIONS.map((n) => (
                <option key={n} value={n}>H{n}</option>
              ))}
            </select>
          </div>

          <hr className="my-4" style={{ borderTop: '1px solid var(--sl-border-secondary)' }} />

          <div>
            <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
              <strong>Hold Promotion</strong>
            </p>
            <p className="m-0 small text-muted">
              Choose how calendar holds behave when deleted.
            </p>
          </div>
          <div className="mt-3 d-flex flex-column" style={{ gap: 12, maxWidth: 800 }}>
            {PROMOTION_OPTIONS.map((option) => {
              const isSelected = holdPromotionLevel === option.value;
              return (
                <label
                  key={option.value}
                  className="d-flex align-items-start m-0 p-3 rounded"
                  style={{
                    border: isSelected
                      ? '2px solid var(--sl-fg-brand)'
                      : '1px solid var(--sl-border-primary)',
                    cursor: 'pointer',
                    gap: 12,
                  }}
                >
                  <input
                    type="radio"
                    name="hold-promotion-level"
                    checked={isSelected}
                    onChange={() => handlePromotionChange(option.value)}
                    className="team-radio mt-1"
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                      {option.label}
                    </div>
                    <div className="small text-muted" style={{ marginTop: 2 }}>
                      {option.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <hr className="my-4" style={{ borderTop: '1px solid var(--sl-border-secondary)' }} />

          <div>
            <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
              <strong>Calendar Grouping</strong>
            </p>
            <p className="m-0 small text-muted">
              Choose how holds and confirms are ordered on the calendar across venues.
            </p>
          </div>
          <div className="mt-3 d-flex flex-column" style={{ gap: 12, maxWidth: 800 }}>
            {GROUPING_OPTIONS.map((option) => {
              const isSelected = calendarHoldGrouping === option.value;
              return (
                <label
                  key={option.value}
                  className="d-flex align-items-start m-0 p-3 rounded"
                  style={{
                    border: isSelected
                      ? '2px solid var(--sl-fg-brand)'
                      : '1px solid var(--sl-border-primary)',
                    cursor: 'pointer',
                    gap: 12,
                  }}
                >
                  <input
                    type="radio"
                    name="calendar-hold-grouping"
                    checked={isSelected}
                    onChange={() => handleGroupingChange(option.value)}
                    className="team-radio mt-1"
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                      {option.label}
                    </div>
                    <div className="small text-muted" style={{ marginTop: 2 }}>
                      {option.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <hr className="my-4" style={{ borderTop: '1px solid var(--sl-border-secondary)' }} />

          <div>
            <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
              <strong>Copy Hold Dates</strong>
            </p>
            <p className="m-0 small text-muted">
              Customize the text copied to your clipboard when copying hold dates.
            </p>
          </div>
          <div className="mt-3 d-flex flex-column" style={{ gap: 12, maxWidth: 800 }}>
            <label
              className="d-flex align-items-start m-0 p-3 rounded"
              style={{
                border: '1px solid var(--sl-border-primary)',
                cursor: 'pointer',
                gap: 12,
              }}
            >
              <input
                type="checkbox"
                checked={appendAvailabilityDisclaimer}
                onChange={handleAvailabilityDisclaimerChange}
                className="team-checkbox mt-1"
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                  Append Availability Disclaimer
                </div>
                <div className="small text-muted" style={{ marginTop: 2 }}>
                  Add "Dates not listed are not available." as the last line when copying hold dates.
                </div>
              </div>
            </label>
          </div>

          <hr className="my-4" style={{ borderTop: '1px solid var(--sl-border-secondary)' }} />

          <div>
            <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
              <strong>Removing Holds</strong>
            </p>
            <p className="m-0 small text-muted">
              Choose what people must fill in when they remove a hold from the calendar.
            </p>
          </div>
          <div className="mt-3 d-flex flex-column" style={{ gap: 12, maxWidth: 800 }}>
            <label
              className="d-flex align-items-start m-0 p-3 rounded"
              style={{
                border: '1px solid var(--sl-border-primary)',
                cursor: 'pointer',
                gap: 12,
              }}
            >
              <Checkbox
                id="require-hold-removal-reason"
                checked={requireHoldRemovalReason}
                onChange={handleRequireHoldRemovalReasonChange}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                  Require a reason to remove holds
                </div>
                <div className="small text-muted" style={{ marginTop: 2 }}>
                  A reason must be picked before a hold can be removed. Turn this off to make the reason optional.
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </SettingsPageWrapper>
  );
};

export default SettingsHoldsDetails;
